import { z } from 'zod';
import { FlavorSchema } from './common';
import { HANDLE_RE } from './profile';
import { WHISPER_IMAGE_MAX_BYTES, WHISPER_IMAGE_TYPES } from './whisper';

/**
 * Nearmirer Pro — the user-facing contract. **Mobile-safe**: part of the normal
 * user contract, never imports from `admin/`. Pro is admin-granted; only an
 * `active` user ever sees the Pro tab / DM flow. The moderation queue lives in
 * `admin/pro.ts`.
 */

/** Re-export so the Pro image routes and the client share one source. */
export const PRO_IMAGE_MAX_BYTES = WHISPER_IMAGE_MAX_BYTES;
export const PRO_IMAGE_TYPES = WHISPER_IMAGE_TYPES;
export const PRO_MESSAGE_MAX = 2000;
/** "online" if the other side was active within this many seconds. */
export const PRO_ONLINE_WINDOW_SEC = 60;

export const ProStatusSchema = z.enum(['none', 'requested', 'active', 'denied', 'revoked']);
export type ProStatus = z.infer<typeof ProStatusSchema>;

/* GET /api/pro/status */
export const ProStatusResponseSchema = z.object({
  status: ProStatusSchema,
  /** true when the user may (re-)submit a request right now. */
  canRequest: z.boolean(),
  /** Moderator's note on a denial, if any. */
  decisionNote: z.string().nullable(),
});
export type ProStatusResponse = z.infer<typeof ProStatusResponseSchema>;

/* POST /api/pro/request */
export const RequestProSchema = z.object({
  reason: z.string().trim().max(300).optional(),
});
export type RequestPro = z.infer<typeof RequestProSchema>;

/** The other person in a Pro DM — only what a profile already shows publicly. */
export const ProPeerSchema = z.object({
  handle: z.string(),
  vibeEmoji: z.string().max(8),
  flavor: FlavorSchema,
  pronouns: z.string().max(20).nullable(),
});
export type ProPeer = z.infer<typeof ProPeerSchema>;

/* GET /api/pro/lookup?handle= */
export const ProLookupResponseSchema = ProPeerSchema.extend({
  /** An existing conversation with this person, if there is one. */
  conversationId: z.string().nullable(),
});
export type ProLookupResponse = z.infer<typeof ProLookupResponseSchema>;

/* POST /api/pro/conversations  { handle } */
export const StartProConversationSchema = z.object({
  handle: z.string().regex(HANDLE_RE),
});
export type StartProConversation = z.infer<typeof StartProConversationSchema>;

export const ProConversationSummarySchema = z.object({
  id: z.string(),
  other: ProPeerSchema,
  lastPreview: z.string().max(120),
  lastMessageAt: z.iso.datetime(),
  unread: z.boolean(),
  otherOnline: z.boolean(),
});
export type ProConversationSummary = z.infer<typeof ProConversationSummarySchema>;

/* GET /api/pro/conversations */
export const ProConversationsResponseSchema = z.object({
  conversations: z.array(ProConversationSummarySchema),
});
export type ProConversationsResponse = z.infer<typeof ProConversationsResponseSchema>;

export const ProMessageSchema = z.object({
  id: z.string(),
  from: z.enum(['me', 'them']),
  text: z.string().max(PRO_MESSAGE_MAX),
  /** Opaque R2 key for an image message; `text` is empty when set. */
  imageKey: z.string().nullable(),
  /** true when a moderator removed the image — show a placeholder, not a broken img. */
  imageRemoved: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type ProMessage = z.infer<typeof ProMessageSchema>;

/* GET /api/pro/conversations/:id  (+ ?before= for older pages) */
export const ProThreadResponseSchema = z.object({
  conversation: ProConversationSummarySchema,
  messages: z.array(ProMessageSchema),
  /** The other side's read watermark — drives the read tick on my messages. */
  otherLastReadAt: z.iso.datetime(),
  otherOnline: z.boolean(),
  otherLastActiveAt: z.iso.datetime().nullable(),
  /** true when an older page can still be loaded. */
  hasMoreBefore: z.boolean(),
});
export type ProThreadResponse = z.infer<typeof ProThreadResponseSchema>;

/* GET /api/pro/conversations/:id/messages?after= */
export const ProMessagesResponseSchema = z.object({
  messages: z.array(ProMessageSchema),
  otherLastReadAt: z.iso.datetime(),
  otherOnline: z.boolean(),
});
export type ProMessagesResponse = z.infer<typeof ProMessagesResponseSchema>;

/* POST /api/pro/conversations/:id/messages */
export const PostProMessageSchema = z.object({
  text: z.string().trim().min(1).max(PRO_MESSAGE_MAX),
});
export type PostProMessage = z.infer<typeof PostProMessageSchema>;
